import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import {
  ClipboardCheck, Plus, Trash2, ChevronUp, ChevronDown, Edit, Copy,
  Zap, Wrench, FileText, GripVertical, Save, X, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_TYPES = [
  { value: 'check', label: i18n.t('auto.lbl.checkPassFailNA') },
  { value: 'test', label: i18n.t('auto.lbl.testPassFailNAValue') },
  { value: 'text', label: i18n.t('auto.lbl.textFreeForm') },
  { value: 'subheader', label: i18n.t('auto.lbl.subheaderBoldGroupLabel') },
  { value: 'signature', label: i18n.t('auto.lbl.signatureStripPerWeekday') },
];

const SECTION_TYPES = [
  { value: 'checklist', label: i18n.t('auto.lbl.checklist') },
  { value: 'test_results', label: i18n.t('auto.lbl.testResults') },
  { value: 'documents', label: i18n.t('auto.lbl.documents') },
  { value: 'text', label: i18n.t('auto.lbl.textNotes') },
  { value: 'notes', label: i18n.t('auto.lbl.notesOnlyNoInputFields') },
  { value: 'weekly_grid', label: i18n.t('auto.lbl.weeklyGrid7DaysPF') },
  { value: 'defect_log', label: i18n.t('auto.lbl.defectLogFaultTable') },
  { value: 'equipment_status', label: i18n.t('auto.lbl.equipmentStatusRadio') },
];

const ChecklistTemplateSettings = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/checklists/templates`);
      setTemplates(resp.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('auto.set.deleteThisTemplateExistingChecklistsUsing'))) return;
    try {
      await axios.delete(`${API}/checklists/templates/${id}`);
      toast.success(t('auto.set.templateDeleted'));
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleDuplicate = async (tpl) => {
    try {
      const copy = {
        name: `${tpl.name} (Copy)`,
        description: tpl.description || null,
        doc_ref: `${tpl.doc_ref}-COPY`,
        revision: tpl.revision,
        type: tpl.type,
        orientation: tpl.orientation || 'portrait',
        discipline: tpl.discipline,
        phase: tpl.phase,
        header_fields: tpl.header_fields,
        sections: tpl.sections,
        sign_off_roles: tpl.sign_off_roles,
        strict_header_fields: !!tpl.strict_header_fields,
      };
      await axios.post(`${API}/checklists/templates`, copy);
      toast.success(t('auto.set.templateDuplicated'));
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to duplicate'); }
  };

  const openEditor = (tpl) => {
    setEditingTemplate(tpl ? JSON.parse(JSON.stringify(tpl)) : {
      name: '', description: '', doc_ref: '', revision: 'Rev 01', type: 'checklist',
      orientation: 'portrait',
      discipline: 'electrical', phase: 'during_installation',
      header_fields: [
        { key: 'project_name', label: t('auto.lbl.projectNameSite'), type: 'text', required: true },
        { key: 'client', label: t('auto.lbl.clientMainContractor'), type: 'text', required: true },
      ],
      sections: [],
      sign_off_roles: [],
      strict_header_fields: true,
    });
    setShowEditor(true);
  };

  const handleSaveTemplate = async (tplData) => {
    try {
      if (tplData.id) {
        await axios.put(`${API}/checklists/templates/${tplData.id}`, tplData);
        toast.success(t('auto.set.templateUpdated'));
      } else {
        await axios.post(`${API}/checklists/templates`, tplData);
        toast.success(t('auto.set.templateCreated'));
      }
      setShowEditor(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  if (loading) return <div className="text-sm text-gray-400 p-4">{t('auto.set.loading2')}</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                {t('auto.set.checklistTemplates')}
              </CardTitle>
              <CardDescription>
                {t('auto.set.defineChecklistAndReportTemplatesFor')}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (!window.confirm(
                    'Import the 5 standard weekly pre-use inspection templates?\n\n' +
                    '• Forklift\n• Boom MEWP\n• Telehandler\n• Scissor Lift\n• Electric Pallet Truck\n\n' +
                    'Existing templates with matching doc refs are left untouched.'
                  )) return;
                  try {
                    const resp = await axios.post(`${API}/checklists/templates/bulk-import-standard`);
                    const d = resp.data || {};
                    toast.success(d.message || 'Bulk import complete');
                    fetchTemplates();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Bulk import failed');
                  }
                }}
                data-testid="bulk-import-standard-btn"
              >
                <Download className="h-4 w-4 mr-1" /> {t('auto.set.importStandardSet')}
              </Button>
              <Button size="sm" onClick={() => openEditor(null)} data-testid="new-template-btn">
                <Plus className="h-4 w-4 mr-1" /> {t('auto.set.newTemplate')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {templates.map(tpl => {
              const DIcon = tpl.discipline === 'electrical' ? Zap : tpl.discipline === 'mechanical' ? Wrench : FileText;
              const itemCount = tpl.sections?.reduce((a, s) => a + (s.items?.length || 0), 0) || 0;
              return (
                <div key={tpl.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 group" data-testid={`template-row-${tpl.id}`}>
                  <DIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{tpl.name}</span>
                      <span className="font-mono text-xs text-gray-400">{tpl.doc_ref}</span>
                      {tpl.is_default && <Badge variant="outline" className="text-xs">{t('auto.set.default')}</Badge>}
                    </div>
                    <p className="text-xs text-gray-500">
                      {tpl.description ? `${tpl.description} · ` : ''}
                      {tpl.sections?.length || 0} sections, {itemCount} items, {tpl.sign_off_roles?.length || 0} sign-off roles
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(tpl)} title={t('auto.set.duplicate')}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditor(tpl)} title={t('auto.set.edit')}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(tpl.id)} title={t('auto.set.delete')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
            {templates.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">{t('auto.set.noTemplatesYet')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Template Editor Dialog */}
      {showEditor && editingTemplate && (
        <TemplateEditorDialog
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
        />
      )}
    </div>
  );
};

// ==================== Template Editor Dialog ====================
const TemplateEditorDialog = ({ template, onSave, onClose }) => {
  const { t } = useTranslation();
  const [tpl, setTpl] = useState(template);

  const updateField = (key, value) => setTpl(prev => ({ ...prev, [key]: value }));

  // Section helpers
  const addSection = () => {
    setTpl(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { title: '', reference: '', type: 'checklist', items: [] }],
    }));
  };

  const updateSection = (idx, key, value) => {
    setTpl(prev => {
      const sections = [...prev.sections];
      sections[idx] = { ...sections[idx], [key]: value };
      return { ...prev, sections };
    });
  };

  const removeSection = (idx) => {
    setTpl(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
  };

  const moveSection = (idx, dir) => {
    setTpl(prev => {
      const sections = [...prev.sections];
      const target = idx + dir;
      if (target < 0 || target >= sections.length) return prev;
      [sections[idx], sections[target]] = [sections[target], sections[idx]];
      return { ...prev, sections };
    });
  };

  // Item helpers
  const addItem = (sIdx) => {
    setTpl(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = { ...sections[sIdx], items: [...sections[sIdx].items, { label: '', hint: '', type: 'check' }] };
      return { ...prev, sections };
    });
  };

  const updateItem = (sIdx, iIdx, key, value) => {
    setTpl(prev => {
      const sections = JSON.parse(JSON.stringify(prev.sections));
      sections[sIdx].items[iIdx][key] = value;
      return { ...prev, sections };
    });
  };

  const removeItem = (sIdx, iIdx) => {
    setTpl(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = { ...sections[sIdx], items: sections[sIdx].items.filter((_, i) => i !== iIdx) };
      return { ...prev, sections };
    });
  };

  // Move an item up (dir=-1) or down (dir=1) within its section.
  // Silently no-ops if the target index would fall outside the array.
  const moveItem = (sIdx, iIdx, dir) => {
    setTpl(prev => {
      const sections = JSON.parse(JSON.stringify(prev.sections));
      const items = sections[sIdx].items;
      const j = iIdx + dir;
      if (j < 0 || j >= items.length) return prev;
      [items[iIdx], items[j]] = [items[j], items[iIdx]];
      return { ...prev, sections };
    });
  };

  // Header field helpers
  const addHeaderField = () => {
    setTpl(prev => ({
      ...prev,
      header_fields: [...(prev.header_fields || []), { key: '', label: '', type: 'text', required: false, placeholder: '' }],
    }));
  };

  const updateHeaderField = (idx, key, value) => {
    setTpl(prev => {
      const fields = [...prev.header_fields];
      fields[idx] = { ...fields[idx], [key]: value };
      if (key === 'label') fields[idx].key = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      return { ...prev, header_fields: fields };
    });
  };

  const removeHeaderField = (idx) => {
    setTpl(prev => ({ ...prev, header_fields: prev.header_fields.filter((_, i) => i !== idx) }));
  };

  // Sign-off role helpers
  const addSignOffRole = () => {
    setTpl(prev => ({ ...prev, sign_off_roles: [...(prev.sign_off_roles || []), { role: '' }] }));
  };

  const updateSignOffRole = (idx, value) => {
    setTpl(prev => {
      const roles = [...prev.sign_off_roles];
      roles[idx] = { role: value };
      return { ...prev, sign_off_roles: roles };
    });
  };

  const removeSignOffRole = (idx) => {
    setTpl(prev => ({ ...prev, sign_off_roles: prev.sign_off_roles.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = () => {
    if (!tpl.name?.trim()) { toast.error(t('auto.set.templateNameIsRequired')); return; }
    if (!tpl.doc_ref?.trim()) { toast.error(t('auto.set.documentReferenceIsRequired')); return; }
    onSave(tpl);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tpl.id ? t('auto.set.editTemplate') : t('auto.set.newChecklistTemplate')}</DialogTitle>
          <DialogDescription>{t('auto.set.defineTheStructureForInstallationChecklists')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium">{t('auto.set.templateName')}</label>
              <Input value={tpl.name || ''} onChange={(e) => updateField('name', e.target.value)} placeholder="e.g. Electrical Inspection Checklist" />
            </div>
            <div>
              <label className="text-xs font-medium">{t('auto.set.docReference')}</label>
              <Input value={tpl.doc_ref || ''} onChange={(e) => updateField('doc_ref', e.target.value)} placeholder="e.g. EFX-EL-CL-002" className="font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium">{t('auto.set.revision')}</label>
              <Input value={tpl.revision || ''} onChange={(e) => updateField('revision', e.target.value)} placeholder={t('auto.set.rev01')} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <label className="text-xs font-medium">{t('auto.set.description')} <span className="text-gray-400">{t('auto.set.shownOnTheChecklistListAnd')}</span></label>
              <Input
                value={tpl.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="e.g. Weekly pre-use inspection — 2.5t diesel forklift"
                data-testid="template-description-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium">{t('auto.set.type')}</label>
              <Select value={tpl.type} onValueChange={(v) => updateField('type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checklist">{t('auto.set.checklist')}</SelectItem>
                  <SelectItem value="report">{t('auto.set.report')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">{t('auto.set.discipline')}</label>
              <Select value={tpl.discipline} onValueChange={(v) => updateField('discipline', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="electrical">{t('auto.set.electrical')}</SelectItem>
                  <SelectItem value="mechanical">{t('auto.set.mechanical')}</SelectItem>
                  <SelectItem value="general">{t('auto.set.general')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">{t('auto.set.phase')}</label>
              <Select value={tpl.phase} onValueChange={(v) => updateField('phase', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="during_installation">{t('auto.set.duringInstallation')}</SelectItem>
                  <SelectItem value="commissioning_handover">{t('auto.set.commissioningHandover')}</SelectItem>
                  <SelectItem value="pre_installation">{t('auto.set.preInstallation')}</SelectItem>
                  <SelectItem value="maintenance">{t('auto.set.maintenance')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium">{t('auto.set.pageOrientation')}</label>
              <Select value={tpl.orientation || 'portrait'} onValueChange={(v) => updateField('orientation', v)}>
                <SelectTrigger data-testid="template-orientation-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">{t('auto.set.portraitA4')}</SelectItem>
                  <SelectItem value="landscape">{t('auto.set.landscapeA4NeededForWeeklyGrid')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium">{t('auto.set.equipmentAssetLink')}</label>
              <Select value={tpl.asset_link || 'optional'} onValueChange={(v) => updateField('asset_link', v)}>
                <SelectTrigger data-testid="template-asset-link-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.set.hiddenNotAboutASpecificItem')}</SelectItem>
                  <SelectItem value="optional">{t('auto.set.optionalPickerShownMayBeLeft')}</SelectItem>
                  <SelectItem value="required">{t('auto.set.requiredSignOffBlockedUntilAn')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                When linked, the finished sheet appears on that item's record in Inventory and,
                once signed off, on its public QR page. Use <strong>{t('auto.set.required')}</strong> for plant and
                power-tool checks — a weekly forklift sheet that doesn't name the forklift proves nothing.
              </p>
            </div>
          </div>

          {/* Header Fields */}
          <div>
            <label
              className="mb-2 flex items-start gap-2 rounded-lg border bg-gray-50 p-2.5 cursor-pointer select-none"
              data-testid="template-strict-headers-row"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#AA0000] cursor-pointer"
                checked={!!tpl.strict_header_fields}
                onChange={(e) => updateField('strict_header_fields', e.target.checked)}
                data-testid="template-strict-headers"
              />
              <span className="text-xs">
                <span className="font-semibold">{t('auto.set.requireHeaderFieldsBeforeSignOff')}</span>
                <span className="block text-gray-500 mt-0.5">
                  Blocks sign-off until every field marked <strong>{t('auto.set.req')}</strong> below
                  is filled in. Admins and whoever created the checklist can still
                  sign off, so nobody gets locked out of their own document.
                </span>
              </span>
            </label>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">{t('auto.set.headerFields')}</h3>              <Button variant="ghost" size="sm" onClick={addHeaderField} data-testid="btn-addheaderfield"><Plus className="h-3 w-3 mr-1" /> {t('auto.set.add')}</Button>
            </div>
            <div className="space-y-1">
              {(tpl.header_fields || []).map((field, idx) => (
                <div key={idx} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded">
                  <Input className="h-7 text-xs flex-1" value={field.label} onChange={(e) => updateHeaderField(idx, 'label', e.target.value)} placeholder={t('auto.set.label')} />
                  <Select value={field.type || 'text'} onValueChange={(v) => updateHeaderField(idx, 'type', v)}>
                    <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">{t('auto.set.text')}</SelectItem>
                      <SelectItem value="date">{t('auto.set.date')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input className="h-7 text-xs w-32" value={field.placeholder || ''} onChange={(e) => updateHeaderField(idx, 'placeholder', e.target.value)} placeholder={t('auto.set.placeholder')} />
                  <label
                    className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none shrink-0"
                    title={t('auto.set.requiredAnAmberReminderShowsOn')}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-[#AA0000] cursor-pointer"
                      checked={!!field.required}
                      onChange={(e) => updateHeaderField(idx, 'required', e.target.checked)}
                      data-testid={`headerfield-required-${idx}`}
                    />
                    {t('auto.set.req')}
                  </label>
                  <button className="text-red-400 hover:text-red-600" onClick={() => removeHeaderField(idx)}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Sections ({tpl.sections?.length || 0})</h3>
              <Button variant="ghost" size="sm" onClick={addSection} data-testid="btn-addsection"><Plus className="h-3 w-3 mr-1" /> {t('auto.set.addSection')}</Button>
            </div>
            <div className="space-y-3">
              {(tpl.sections || []).map((section, sIdx) => (
                <Card key={sIdx} className="border-l-4 border-l-blue-200">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-5">{sIdx + 1}.</span>
                      <Input className="h-7 text-sm flex-1 font-medium" value={section.title} onChange={(e) => updateSection(sIdx, 'title', e.target.value)} placeholder={t('auto.set.sectionTitle')} />
                      <Input className="h-7 text-xs w-40" value={section.reference || ''} onChange={(e) => updateSection(sIdx, 'reference', e.target.value)} placeholder={t('auto.set.refQaPolicyX')} />
                      <Select value={section.type || 'checklist'} onValueChange={(v) => updateSection(sIdx, 'type', v)}>
                        <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SECTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button className="text-gray-300 hover:text-gray-600" onClick={() => moveSection(sIdx, -1)}><ChevronUp className="h-3.5 w-3.5" /></button>
                      <button className="text-gray-300 hover:text-gray-600" onClick={() => moveSection(sIdx, 1)}><ChevronDown className="h-3.5 w-3.5" /></button>
                      <button className="text-red-300 hover:text-red-600" onClick={() => removeSection(sIdx)}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    {/* Items */}
                    <div className="pl-6 space-y-1">
                      {(section.items || []).map((item, iIdx) => (
                        <div key={iIdx} className="flex items-center gap-2">
                          <span className="text-xs text-gray-300 w-4">{iIdx + 1}</span>
                          <Input className="h-6 text-xs flex-1" value={item.label} onChange={(e) => updateItem(sIdx, iIdx, 'label', e.target.value)} placeholder={t('auto.set.itemLabel')} />
                          <Input className="h-6 text-xs w-40" value={item.hint || ''} onChange={(e) => updateItem(sIdx, iIdx, 'hint', e.target.value)} placeholder={t('auto.set.hintCriteria')} />
                          <Select value={item.type || 'check'} onValueChange={(v) => updateItem(sIdx, iIdx, 'type', v)}>
                            <SelectTrigger className="h-6 w-24 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-0.5">
                            <button
                              type="button"
                              disabled={iIdx === 0}
                              onClick={() => moveItem(sIdx, iIdx, -1)}
                              className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={t('auto.set.moveUp')}
                              data-testid={`item-move-up-${sIdx}-${iIdx}`}
                            ><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button
                              type="button"
                              disabled={iIdx === (section.items?.length || 0) - 1}
                              onClick={() => moveItem(sIdx, iIdx, 1)}
                              className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={t('auto.set.moveDown')}
                              data-testid={`item-move-down-${sIdx}-${iIdx}`}
                            ><ChevronDown className="h-3.5 w-3.5" /></button>
                          </div>
                          <button className="text-red-300 hover:text-red-500" onClick={() => removeItem(sIdx, iIdx)} data-testid={`item-remove-${sIdx}-${iIdx}`}><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-400" onClick={() => addItem(sIdx)}>
                        <Plus className="h-3 w-3 mr-1" /> {t('auto.set.addItem')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Sign-Off Roles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">{t('auto.set.signOffRoles')}</h3>
              <Button variant="ghost" size="sm" onClick={addSignOffRole} data-testid="btn-addsignoffrole"><Plus className="h-3 w-3 mr-1" /> {t('auto.set.addRole')}</Button>
            </div>
            <div className="space-y-1">
              {(tpl.sign_off_roles || []).map((role, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input className="h-7 text-sm flex-1" value={role.role} onChange={(e) => updateSignOffRole(idx, e.target.value)} placeholder="e.g. Electrical Supervisor (Expertfix)" />
                  <button className="text-red-300 hover:text-red-500" onClick={() => removeSignOffRole(idx)}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel-btn">{t('auto.set.cancel')}</Button>
          <Button onClick={handleSubmit} data-testid="dialog-submit-btn">
            <Save className="h-4 w-4 mr-1" /> {tpl.id ? t('auto.set.saveChanges') : t('auto.set.createTemplate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistTemplateSettings;
